import { DatePlan, DateStop, DatePlannerState, Place, TransportLeg } from '../types';
import { PLACES } from '../data/places';
import { calculateDistance, estimateTravelTime } from '../data/transport';

interface PlaceScore {
  place: Place;
  score: number;
}

export class DateGenerator {
  private places: Place[];
  private state: DatePlannerState;
  private excludedPlaceIds: Set<string>;

  constructor(state: DatePlannerState, excludedPlaceIds: string[] = []) {
    this.places = PLACES;
    this.state = state;
    this.excludedPlaceIds = new Set(excludedPlaceIds);
  }

  generateDatePlans(): DatePlan[] {
    const candidates = this.filterCandidatePlaces();
    const sequences = this.buildDateSequences(candidates);
    const plans = sequences.map((seq, idx) => this.buildDatePlan(seq, idx));
    
    return plans.sort((a, b) => b.compatibility - a.compatibility);
  }

  private filterCandidatePlaces(): Place[] {
    return this.places.filter(place => {
      // Exclude previously visited places
      if (this.excludedPlaceIds.has(place.id)) return false;

      // Filter by opening hours - must be open during the date
      const [openHour] = place.openingHours.open.split(':').map(Number);
      const [closeHour] = place.openingHours.close.split(':').map(Number);
      const startHour = parseInt(this.state.startTime.split(':')[0]);
      
      // Very basic check - in real app, would be more sophisticated
      if (startHour > closeHour || (startHour + (this.state.duration / 60)) > closeHour) {
        // Allow if place closes after we'd leave
        if (closeHour < startHour) return false;
      }

      // Filter by vibes
      const hasMatchingVibe = this.state.vibes.length === 0 || 
        place.dateVibes.some(v => this.state.vibes.some(sv => sv.toLowerCase().includes(v.toLowerCase())));
      if (this.state.vibes.length > 0 && !hasMatchingVibe) return false;

      // Filter by indoor/outdoor preference
      if (this.state.indoorOutdoor !== 'any') {
        if (this.state.indoorOutdoor === 'indoor' && place.indoorOutdoor === 'outdoor') return false;
        if (this.state.indoorOutdoor === 'outdoor' && place.indoorOutdoor === 'indoor') return false;
      }

      // Filter by budget
      if (place.entryFee > this.state.budget * 0.5) return false;

      return true;
    });
  }

  private buildDateSequences(candidates: Place[]): Place[][] {
    const sequences: Place[][] = [];
    
    // Generate different sequence types
    sequences.push(this.buildRomanticSequence(candidates));
    sequences.push(this.buildCultureSequence(candidates));
    sequences.push(this.buildFoodSequence(candidates));
    sequences.push(this.buildAdventureSequence(candidates));
    sequences.push(this.buildPeacefulSequence(candidates));
    
    return sequences.filter(seq => seq.length >= 2);
  }

  private buildRomanticSequence(candidates: Place[]): Place[] {
    return candidates
      .sort((a, b) => b.romanticScore - a.romanticScore)
      .slice(0, 5);
  }

  private buildCultureSequence(candidates: Place[]): Place[] {
    return candidates
      .sort((a, b) => b.cultureScore - a.cultureScore)
      .slice(0, 4);
  }

  private buildFoodSequence(candidates: Place[]): Place[] {
    return candidates
      .sort((a, b) => b.foodScore - a.foodScore)
      .slice(0, 4);
  }

  private buildAdventureSequence(candidates: Place[]): Place[] {
    return candidates
      .sort((a, b) => b.activityScore - a.activityScore)
      .slice(0, 5);
  }

  private buildPeacefulSequence(candidates: Place[]): Place[] {
    return candidates
      .filter(p => !p.crowdLevel || p.crowdLevel !== 'high')
      .sort((a, b) => (b.scenicScore + b.romanticScore) / 2 - (a.scenicScore + a.romanticScore) / 2)
      .slice(0, 4);
  }

  private buildDatePlan(sequence: Place[], index: number): DatePlan {
    const stops = this.createDateStops(sequence);
    const totalDuration = stops.reduce((sum, stop) => sum + stop.duration + (stop.transportToNext?.duration || 0), 0);
    const totalCost = stops.reduce((sum, stop) => sum + stop.place.entryFee + stop.place.estimatedSpend, 0);
    const totalWalking = stops.reduce((sum, stop) => sum + (stop.transportToNext?.distance || 0), 0);
    const publicTransportPercentage = this.calculatePublicTransportPercentage(stops);
    const noveltyScore = this.calculateNoveltyScore(sequence);
    const compatibility = this.calculateCompatibility(totalDuration, totalCost, stops.length);

    const title = this.generateTitle(sequence, index);
    const story = this.generateStory(sequence, title);

    return {
      id: `plan-${Date.now()}-${index}`,
      title,
      subtitle: this.generateSubtitle(sequence),
      story,
      vibe: this.determineVibes(sequence),
      stops,
      estimatedCost: totalCost,
      totalDuration,
      totalWalking,
      publicTransportPercentage,
      noveltyScore,
      compatibility,
      startLocation: this.state.startLocation,
      endLocation: this.state.endLocation,
      date: this.state.date,
      startTime: this.state.startTime,
    };
  }

  private createDateStops(sequence: Place[]): DateStop[] {
    const stops: DateStop[] = [];
    let currentTime = this.parseTime(this.state.startTime);

    for (let i = 0; i < sequence.length; i++) {
      const place = sequence[i];
      const duration = this.adjustDurationForBudget(place.estimatedVisitDuration);
      
      const startTime = this.formatTime(currentTime);
      const stop: DateStop = {
        id: `stop-${i}`,
        placeId: place.id,
        place,
        startTime,
        duration,
        purpose: this.determinePurpose(place),
        transportToNext: i < sequence.length - 1 ? this.createTransportLeg(place, sequence[i + 1]) : undefined,
      };

      stops.push(stop);
      
      // Update current time
      currentTime += duration;
      if (stop.transportToNext) {
        currentTime += stop.transportToNext.duration;
      }
    }

    return stops;
  }

  private createTransportLeg(from: Place, to: Place): TransportLeg {
    const distance = calculateDistance(from.latitude, from.longitude, to.latitude, to.longitude);
    
    // Choose best transport mode based on preferences
    let mode: TransportLeg['mode'] = 'metro';
    if (this.state.transportModes.includes('walking') && distance < 1.5) {
      mode = 'walking';
    } else if (this.state.transportModes.includes('metro') && distance > 1.5) {
      mode = 'metro';
    } else if (this.state.transportModes.includes('local_train')) {
      mode = 'local_train';
    } else if (this.state.transportModes.includes('bus')) {
      mode = 'bus';
    }

    const duration = estimateTravelTime(distance, mode);
    
    const costs: Record<TransportLeg['mode'], number> = {
      'metro': 15,
      'local_train': 10,
      'bus': 8,
      'walking': 0,
      'ferry': 20,
      'auto': 30,
      'cab': 50,
    };

    return {
      from: from.name,
      to: to.name,
      mode,
      duration: Math.max(duration, 5),
      cost: costs[mode] || 20,
      distance,
      changes: Math.random() > 0.7 ? 1 : 0,
      instructions: `Travel from ${from.name} to ${to.name} via ${mode}.`,
      easyForCouples: distance < 3 || mode === 'walking',
    };
  }

  private adjustDurationForBudget(baseDuration: number): number {
    // Reduce durations if we have limited time
    if (this.state.duration < 300) { // Less than 5 hours
      return Math.floor(baseDuration * 0.7);
    }
    return baseDuration;
  }

  private determinePurpose(place: Place): string {
    const purposes: Record<string, string> = {
      'heritage': 'Heritage exploration',
      'museum': 'Cultural immersion',
      'art': 'Art appreciation',
      'food': 'Food experience',
      'park': 'Nature time',
      'cafe': 'Café break',
      'shopping': 'Exploration',
      'activity': 'Fun activity',
      'scenic': 'Scenic moment',
      'cultural': 'Cultural experience',
    };
    return purposes[place.category] || 'Visit';
  }

  private calculatePublicTransportPercentage(stops: DateStop[]): number {
    let publicTransportTime = 0;
    let totalTime = 0;

    stops.forEach(stop => {
      totalTime += stop.duration;
      if (stop.transportToNext) {
        totalTime += stop.transportToNext.duration;
        if (['metro', 'local_train', 'bus', 'ferry'].includes(stop.transportToNext.mode)) {
          publicTransportTime += stop.transportToNext.duration;
        }
      }
    });

    return totalTime > 0 ? Math.round((publicTransportTime / totalTime) * 100) : 50;
  }

  private calculateNoveltyScore(sequence: Place[]): number {
    // Calculate based on mix of categories and scores
    const categoryCount = new Set(sequence.map(p => p.category)).size;
    const avgScore = sequence.reduce((sum, p) => 
      sum + (p.romanticScore + p.scenicScore + p.cultureScore + p.foodScore + p.activityScore) / 5, 0) / sequence.length;
    
    const novelty = (categoryCount / 10) * 50 + (avgScore / 100) * 50;
    return Math.min(Math.round(novelty), 100);
  }

  private calculateCompatibility(duration: number, cost: number, stops: number): number {
    // Score based on fit within user preferences
    let score = 50;

    // Duration fitness
    const durationDiff = Math.abs(duration - this.state.duration) / this.state.duration;
    score += (1 - Math.min(durationDiff, 1)) * 25;

    // Budget fitness
    const costPerPerson = cost / this.state.numPeople;
    const budgetDiff = Math.abs(costPerPerson - this.state.budget) / this.state.budget;
    score += (1 - Math.min(budgetDiff, 1)) * 25;

    // Variety (more stops is usually better)
    score += Math.min(stops * 5, 25);

    return Math.max(Math.min(Math.round(score), 100), 0);
  }

  private determineVibes(sequence: Place[]): string[] {
    const vibeScores: Record<string, number> = {
      'romantic': 0,
      'cultural': 0,
      'fun-activities': 0,
      'peaceful': 0,
      'photography': 0,
      'food-adventure': 0,
      'intellectual': 0,
      'city-exploration': 0,
    };

    sequence.forEach(place => {
      place.dateVibes.forEach(vibe => {
        vibeScores[vibe] = (vibeScores[vibe] || 0) + 1;
      });
    });

    return Object.entries(vibeScores)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([vibe]) => vibe);
  }

  private generateTitle(sequence: Place[], index: number): string {
    const titles = [
      'Heritage & Sunset',
      'Art & Culture',
      'Street Exploration Adventure',
      'Peaceful Riverside Date',
      'Food & City Vibes',
      'Historic Kolkata Journey',
      'Evening Exploration',
      'Cultural Immersion',
    ];
    return titles[index % titles.length];
  }

  private generateSubtitle(sequence: Place[]): string {
    const categories = [...new Set(sequence.map(p => p.category))];
    return categories.slice(0, 2).join(' + ').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  private generateStory(sequence: Place[], title: string): string {
    if (title.includes('Heritage')) {
      return 'Dive into Kolkata\'s history through iconic landmarks, heritage sites, and riverside views.';
    } else if (title.includes('Art')) {
      return 'Explore the creative side of Kolkata with museums, galleries, and cultural experiences.';
    } else if (title.includes('Food')) {
      return 'Embark on a culinary journey through Kolkata\'s diverse food scene.';
    } else if (title.includes('Peaceful')) {
      return 'Escape the hustle and find tranquility in serene parks and quiet corners.';
    } else if (title.includes('Street')) {
      return 'Wander through Kolkata\'s vibrant streets, discovering local life and hidden gems.';
    }
    return 'Experience the magic of Kolkata together.';
  }

  private parseTime(timeStr: string): number {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  }
}
